/**
 * Serviço de Prefetch Inteligente
 * Aquece cache quando usuário troca de empresa ou página
 * Melhora percepção de velocidade
 */

import { buscarDashboardDetalhado } from 'features/financeiro/services/financeiroService';
import { buscarDadosHistoricos, verificarStatusCache, listarMesesDisponiveis } from 'features/financeiro/services/financeiroService';
import permissionService from 'features/administrativo/services/permissionService';
import api from 'services/api';

class PrefetchService {
  constructor() {
    // Cache de prefetch
    this.prefetchCache = new Map();
    
    // Configurações
    this.prefetchTimeout = 10000; // 10 segundos
    this.maxConcurrentPrefetches = 3;
    this.activePrefetches = new Set();
    
    // Endpoints por contexto
    this.endpointsByContext = {
      financeiro: [
        'buscarDashboardDetalhado',
        'buscarDadosHistoricos',
        'verificarStatusCache',
        'listarMesesDisponiveis'
      ],
      administrativo: [
        'getManageableCompanies',
        'getAvailableGroups',
        'getAllPermissions'
      ],
      operacional: [
        'getOSParaAgendamento',
        'getTecnicosDisponiveis'
      ]
    };
    
    // Iniciar listeners
    this.setupEventListeners();
  }

  /**
   * Configurar listeners de eventos
   */
  setupEventListeners() {
    // Listener para mudança de empresa
    window.addEventListener('company-changed', (event) => {
      const { company, context } = event.detail || {};
      this.prefetchForCompany(company, context);
    });

    // Listener para mudança de contexto
    window.addEventListener('company-context-changed', (event) => {
      const { context } = event.detail || {};
      this.prefetchForContext(context);
    });

    // Listener para navegação iminente (hover em links)
    this.setupNavigationPrefetch();
  }

  /**
   * Configurar prefetch para navegação
   */
  setupNavigationPrefetch() {
    // Prefetch ao hover em links de navegação
    document.addEventListener('mouseover', (event) => {
      const link = event.target.closest('a[href]');
      if (link && this.shouldPrefetchLink(link)) {
        this.prefetchForNavigation(link.href);
      }
    });

    // Prefetch ao focus em links (acessibilidade)
    document.addEventListener('focusin', (event) => {
      const link = event.target.closest('a[href]');
      if (link && this.shouldPrefetchLink(link)) {
        this.prefetchForNavigation(link.href);
      }
    });
  }

  /**
   * Verificar se deve fazer prefetch do link
   */
  shouldPrefetchLink(link) {
    // Apenas links internos
    if (!link.href.startsWith(window.location.origin)) return false;
    
    // Apenas links para páginas principais
    const mainPages = ['/dashboard', '/financeiro', '/comercial', '/operacional', '/administrativo'];
    return mainPages.some(page => link.href.includes(page));
  }

  /**
   * Prefetch para mudança de empresa
   */
  async prefetchForCompany(company, context = 'financeiro') {
    if (!company) return;

    const companyName = company.name || company;
    
    // Prefetch endpoints financeiros
    if (context === 'financeiro' || !context) {
      await this.prefetchFinanceiroEndpoints(companyName);
    }
    
    // Prefetch endpoints administrativos
    if (context === 'administrativo') {
      await this.prefetchAdministrativoEndpoints();
    }
    
    // Prefetch endpoints operacionais
    if (context === 'operacional') {
      await this.prefetchOperacionalEndpoints();
    }
  }

  /**
   * Prefetch para mudança de contexto
   */
  async prefetchForContext(context) {
    // Prefetch baseado no contexto
    switch (context) {
      case 'financeiro':
        await this.prefetchFinanceiroEndpoints();
        break;
      case 'administrativo':
        await this.prefetchAdministrativoEndpoints();
        break;
      case 'operacional':
        await this.prefetchOperacionalEndpoints();
        break;
      default:
        break;
    }
  }

  /**
   * Prefetch para navegação
   */
  async prefetchForNavigation(url) {
    const path = new URL(url).pathname;
    
    // Mapear rotas para contextos
    const routeContexts = {
      '/dashboard': 'financeiro',
      '/financeiro': 'financeiro',
      '/comercial': 'comercial',
      '/operacional': 'operacional',
      '/administrativo': 'administrativo'
    };
    
    const context = routeContexts[path];
    if (context) {
      await this.prefetchForContext(context);
    }
  }

  /**
   * Prefetch endpoints financeiros
   */
  async prefetchFinanceiroEndpoints(companyName = null) {
    const prefetches = [];
    
    // Dashboard detalhado
    if (companyName) {
      // Chavear por empresa para evitar colisões entre empresas diferentes
      const key = `buscarDashboardDetalhado-${companyName}`;
      prefetches.push(
        this.prefetchEndpoint(key, () => 
          buscarDashboardDetalhado({ empresa: companyName })
        )
      );
    }
    
    // Dados históricos
    prefetches.push(
      this.prefetchEndpoint('buscarDadosHistoricos', () => 
        buscarDadosHistoricos({ meses: 12 })
      )
    );
    
    // Status do cache
    prefetches.push(
      this.prefetchEndpoint('verificarStatusCache', () => 
        verificarStatusCache()
      )
    );
    
    // Dados consolidados - removido pois usa financeiroConsolidadoService
    
    // Meses disponíveis
    prefetches.push(
      this.prefetchEndpoint('listarMesesDisponiveis', () => 
        listarMesesDisponiveis()
      )
    );
    
    // Executar prefetches em paralelo (limitado)
    await this.executePrefetches(prefetches);
  }

  /**
   * Prefetch endpoints administrativos
   */
  async prefetchAdministrativoEndpoints() {
    const prefetches = [
      this.prefetchEndpoint('getManageableCompanies', () => 
        permissionService.getManageableCompanies()
      ),
      this.prefetchEndpoint('getAvailableGroups', () => 
        permissionService.getAvailableGroups()
      ),
      this.prefetchEndpoint('getAllPermissions', () => 
        permissionService.getAllPermissions()
      )
    ];
    
    await this.executePrefetches(prefetches);
  }

  /**
   * Prefetch endpoints operacionais
   */
  async prefetchOperacionalEndpoints() {
    // Respeitar permissões do usuário salvas no localStorage (AuthContext)
    let userPerms = [];
    try {
      const savedUser = localStorage.getItem('dashboard_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        userPerms = (parsed?.permissions || []).map(p => p.name);
      }
    } catch {}

    const canAgendamento = userPerms.includes('operacional.agendamentos.view');

    const prefetches = [];

    if (canAgendamento) {
      prefetches.push(
        this.prefetchEndpoint('getOSParaAgendamento', () => 
          api.get('/operacional/os-para-agendamento', {
            params: {
              data_inicio: '2025-01-01',
              data_fim: '2025-12-31',
              status: 'aberto'
            }
          })
        )
      );

      prefetches.push(
        this.prefetchEndpoint('getTecnicosDisponiveis', () => 
          api.get('/operacional/tecnicos-disponiveis', {
            params: {
              data: new Date().toISOString().split('T')[0]
            }
          })
        )
      );
    }

    if (prefetches.length > 0) {
      await this.executePrefetches(prefetches);
    }
  }

  /**
   * Prefetch de endpoint individual
   */
  async prefetchEndpoint(key, endpointFn) {
    // Verificar se já está em cache
    if (this.prefetchCache.has(key)) {
      const cached = this.prefetchCache.get(key);
      if (Date.now() - cached.timestamp < 300000) { // 5 minutos
        return cached.data;
      }
    }
    
    // Verificar se já está sendo prefetchado
    if (this.activePrefetches.has(key)) {
      return null;
    }
    
    // Limitar prefetches concorrentes
    if (this.activePrefetches.size >= this.maxConcurrentPrefetches) {
      return null;
    }
    
    this.activePrefetches.add(key);
    
    try {
      const result = await Promise.race([
        endpointFn(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Prefetch timeout')), this.prefetchTimeout)
        )
      ]);

      // Armazenar no cache
      this.prefetchCache.set(key, {
        data: result,
        timestamp: Date.now()
      });
      
      return result;
    } catch (error) {
      console.warn(`⚠️ Prefetch falhou: ${key}`, error.message);
      return null;
    } finally {
      this.activePrefetches.delete(key);
    }
  }

  /**
   * Executar prefetches com limite de concorrência
   */
  async executePrefetches(prefetches) {
    const chunks = [];
    for (let i = 0; i < prefetches.length; i += this.maxConcurrentPrefetches) {
      chunks.push(prefetches.slice(i, i + this.maxConcurrentPrefetches));
    }
    
    for (const chunk of chunks) {
      await Promise.allSettled(chunk);
    }
  }

  /**
   * Obter dados do cache de prefetch
   */
  getPrefetchedData(key) {
    const cached = this.prefetchCache.get(key);
    if (cached && Date.now() - cached.timestamp < 300000) { // 5 minutos
      return cached.data;
    }
    return null;
  }

  /**
   * Limpar cache de prefetch
   */
  clearPrefetchCache() {
    this.prefetchCache.clear();
    this.activePrefetches.clear();
  }

  /**
   * Obter estatísticas do prefetch
   */
  getStats() {
    return {
      cacheSize: this.prefetchCache.size,
      activePrefetches: this.activePrefetches.size,
      cacheKeys: Array.from(this.prefetchCache.keys()),
      activeKeys: Array.from(this.activePrefetches)
    };
  }

  /**
   * Destruir serviço
   */
  destroy() {
    this.clearPrefetchCache();
    window.removeEventListener('company-changed', this.prefetchForCompany);
    window.removeEventListener('company-context-changed', this.prefetchForContext);
  }
}

// Instância singleton
const prefetchService = new PrefetchService();

export default prefetchService;
